const { CONSENT, MAIN } = require('../../../common/js/constants/cookies');
const { COOKIE_CONSENT } = require('../../../common/js/constants/storage-keys');
const { getCookieOptions } = require('../../../common/js/universal/utils');
const { getNonEssentialCookies, getSanitizableCookies } = require('./cookies-get');
const { cookiesList, consentCategories } = require('./constants');

const sanitizer = require('./sanitizers');

const CONSENT_COOKIE = CONSENT;

const cookieManagerErrors = {
  missingLibrary: 'CookieManager: Missing argument. Cookie library not found.',
  missingRemoveArguments: 'CookieManager: Missing argument. Name not found.',
  missingSetArguments: 'CookieManager: Missing argument. Name not found.',
};

const customCookieEncode = (value) => value;

const defaultSettings = {
  consentValidation: false,
};

class CookieManager {
  /**
   * Create CookieManager.
   * @param {Object} cookieLibrary - [cookie-universal | cookie-universal-nuxt | js-cookie]
   */
  constructor(cookieLibrary, settings = {}) {
    if (!cookieLibrary) {
      throw new Error(cookieManagerErrors.missingLibrary);
    }

    this.settings = Object.assign(defaultSettings, settings);

    this.cookieLibrary = cookieLibrary;
    this.consentGiven = false;
    this.consent = this.getConsent();
    this.ecfgCachedValue = '';
  }

  /**
   * Get the value of cookie with given name
   * NOTE: Don't use this method to get the consent
   * cookie directly: use getConsent() instead
   * @param {string} name - Name of the cookie
   * @param {Object} options - Cookie options
   * @return {string} The cookie value of given name.
   */
  get(name, options) {
    return this.cookieLibrary.get(name, options);
  }

  /**
   * Set given value of cookie with given name
   * @param {string} name - Name of the cookie
   * @param {string|number|boolean} value - Cookie value
   * @param {Object} options - Cookie options
   * @return cookie Library set return
   */
  set(name, value, options) {
    let cookieValue = value;

    if (!name) {
      throw new Error(cookieManagerErrors.missingSetArguments);
    }

    if (this.settings.consentValidation) {
      const cookieToSet = cookiesList
        .find((cookie) => cookie.name === name);

      if (!cookieToSet ||
          !this.consent.includes(cookieToSet.permission)) {
        this.remove(name, options);
        return false;
      }

      const cookieSanitizer = sanitizer[cookieToSet.sanitizer];

      // If a cookie has a sanitizer it is indicating
      // that it has different values which might need consent
      if (cookieSanitizer) {
        cookieValue = cookieSanitizer(this.consent, value);
      }
    }

    return this.cookieLibrary.set(name, cookieValue, options);
  }

  /**
   * Remove cookie with given name
   * @param {string} name - Name of the cookie
   * @param {Object} options - Cookie options
   * @return cookie Library remove return
   */
  remove(name, options) {
    if (!name) {
      throw new Error(cookieManagerErrors.missingRemoveArguments);
    }

    return this.cookieLibrary.remove(name, options);
  }

  /**
   * Accept all cookies
   * Sets permission to set all cookies
   */
  acceptAll() {
    const consent = Object.keys(consentCategories)
      .map((key) => consentCategories[key])
      .join('');

    this.setConsent(consent);

    if (this.ecfgCachedValue) {
      // If the user accepts all consents we try to restore their settings
      this.set(
        MAIN,
        this.ecfgCachedValue,
        { ...getCookieOptions(), encode: customCookieEncode },
      );
    }
  }

  /**
   * Rejects non-essential cookies
   * Sets permission to only set essential cookies
   * and removes cookies/values with different consent
   */
  rejectNonEssential() {
    const consent = consentCategories.ESSENTIAL;
    this.setConsent(consent);
    this.removeNonEssentialCookies();
    this.resetNonEssentialValues();
  }

  /**
   * Removes non-essential cookies
   */
  removeNonEssentialCookies() {
    const nonEssential = getNonEssentialCookies();
    const options = getCookieOptions();
    nonEssential.forEach((cookie) => this.remove(cookie.name, options));
  }

  /**
   * Resets non-essential cookie values
   */
  resetNonEssentialValues() {
    this.cacheECFGCookie();
    getSanitizableCookies().forEach((item) => {
      const cookieSanitizer = sanitizer[item.sanitizer];

      if (!cookieSanitizer) {
        return;
      }

      this.set(
        item.name,
        cookieSanitizer(this.consent, this.get(item.name)),
        { ...getCookieOptions(), encode: customCookieEncode },
      );
    });
  }

  /**
   * Caches ECFG Cookie value
   * to restore non-essential values if user accepts consent
   */
  cacheECFGCookie() {
    this.ecfgCachedValue = this.get(
      MAIN,
      getCookieOptions(),
    );
  }

  /**
   * Set cookie consent to given value
   * NOTE: Don't call setConsent directly
   * use either rejectNonEssential or acceptAll
   * @param {string} consent - New cookie consent
   */
  setConsent(consent) {
    this.consent = consent;
    this.consentGiven = true;
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(COOKIE_CONSENT, consent);
    }
    // TODO (ENGT-636): remove the setting of this cookie
    this.set(CONSENT_COOKIE, consent, getCookieOptions());
  }

  /**
   * Return the user's consent from localStorage or cookie
   * defaults to `null`
   * @return {string} consent
   */
  getConsentResponse() {
    let cookieConsent = null;
    if (typeof localStorage !== 'undefined') {
      cookieConsent = localStorage.getItem(COOKIE_CONSENT);
    }
    cookieConsent = cookieConsent || this.cookieLibrary.get(CONSENT_COOKIE, getCookieOptions());
    return cookieConsent;
  }

  /**
   * Return cookie consent from localStorage or cookie
   * defaults to consentCategories.ESSENTIAL
   * @return {string} consent
   */
  getConsent() {
    const cookieConsent = this.getConsentResponse();
    this.consentGiven = !!cookieConsent;
    return cookieConsent || consentCategories.ESSENTIAL;
  }

  /**
   * Return whether or not cookie consent has been given
   * @return {boolean} consent given
   */
  getConsentGiven() {
    if (this.consentGiven) {
      return true;
    }

    const cookieConsent = this.getConsentResponse();
    this.consentGiven = !!cookieConsent;

    return this.consentGiven;
  }

  /**
   * Set consent validation
   */
  setConsentValidation(validation) {
    this.settings.consentValidation = validation;

    // Migrate consent
    // TODO (ENGT-636): remove this “cookie -> localStorage” migration code
    const cookieConsent = this.cookieLibrary.get(CONSENT_COOKIE, getCookieOptions());
    if (typeof localStorage !== 'undefined' && cookieConsent && !localStorage.getItem(COOKIE_CONSENT)) {
      localStorage.setItem(COOKIE_CONSENT, cookieConsent);
    }

    if (validation &&
       this.consent === consentCategories.ESSENTIAL) {
      this.removeNonEssentialCookies();
      this.resetNonEssentialValues();
    }
  }

  /**
   * Adds personalization cookie consent
   */
  addPersonalizationConsent() {
    // We only want to add personalization consent
    // for users that already gave consent
    if (!this.consentGiven) {
      return;
    }

    if (!this.consent.includes(consentCategories.PERSONALISATION)) {
      this.consent += consentCategories.PERSONALISATION;
      this.setConsent(this.consent);
    }
  }
}

module.exports = CookieManager;
module.exports.customCookieEncode = customCookieEncode;
module.exports.cookieManagerErrors = cookieManagerErrors;
